/**
 * Vague 18.1 — Tab "Fantômes" du Noam Radar
 *
 * Sous-onglet du Noam Radar qui surface les sujets les plus silencieux
 * détectés par le Phantom Detector — version cross-org agrégée.
 * Mode strictement additif : composant autonome, importé dans Radar.jsx.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ghost, RefreshCw, Loader2, ChevronRight, ArrowUpRight, Sparkles,
  HelpCircle, X, Search, AlertCircle, ClipboardList, Users, Wallet,
} from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";

const PHANTOM_TYPE_COLORS = {
  ressource_pivot: "#EF4444",
  capture_economique: "#EF9F27",
  profondeur_causale: "#F4D35E",
  plan_action: "#A78BFA",
  reseau: "#378ADD",
};
const SEVERITY_COLOR = {
  critique: "#EF4444",
  majeur: "#EF9F27",
  mineur: "#F4D35E",
  info: "#7F77DD",
};

export default function PhantomRadarView() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showHelp, setShowHelp] = useState(() => {
    try { return localStorage.getItem("radar:phantoms:help-seen") !== "1"; } catch { return true; }
  });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/workspace/cartographie/phantoms/aggregate");
      setData(r.data);
    } catch (e) {
      setError(fmtError(e, "Chargement fantômes impossible"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="text-zinc-500 text-sm py-8 text-center" data-testid="radar-phantoms-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Agrégation des fantômes…
      </div>
    );
  }
  if (error) {
    return <div className="text-red-400 text-sm" data-testid="radar-phantoms-error">{error}</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="radar-phantoms-view">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 noam-overline text-[#A78BFA]">
            <Ghost className="w-3 h-3" /> Couche fantômes
          </div>
          <h2 className="noam-display text-xl mt-1">Ce qui manque dans vos dossiers</h2>
          <p className="text-zinc-400 text-xs mt-1 max-w-3xl">
            Un <strong className="text-zinc-200">fantôme</strong> = un élément qui <strong className="text-zinc-200">devrait
            exister</strong> dans un dossier (opportunité, entité, risque) mais qui est <strong className="text-zinc-200">absent</strong>.
            Noam scanne vos cartographies, repère ces absences et estime ce que chacune vous coûte tant qu'elle n'est pas comblée.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHelp((v) => !v)}
            className="btn-ghost text-xs"
            data-testid="radar-phantoms-help-toggle"
          >
            <HelpCircle className="w-3.5 h-3.5" /> {showHelp ? "Masquer l'aide" : "C'est quoi un fantôme ?"}
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="btn-ghost text-xs disabled:opacity-50"
            data-testid="radar-phantoms-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
          </button>
        </div>
      </div>

      {showHelp && <PhantomExplainer onClose={() => setShowHelp(false)} />}

      {data.total_phantoms === 0 ? (
        <div className="noam-card p-8 text-center" data-testid="radar-phantoms-empty">
          <Ghost className="w-8 h-8 text-zinc-600 mx-auto" />
          <div className="text-sm text-zinc-300 mt-3">Aucun fantôme détecté</div>
          <p className="text-xs text-zinc-500 mt-2 max-w-md mx-auto">
            Lancez un scan Phantom depuis une <strong>Cartographie 360°</strong> (opportunité, entité, risque)
            pour alimenter ce radar. Chaque scan est automatiquement agrégé ici.
          </p>
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="radar-phantoms-kpis">
            <KPI label="Fantômes" value={data.total_phantoms} color="#A78BFA" />
            <KPI label="Sujets scannés" value={data.total_subjects_scanned} color="#7F77DD" />
            <KPI
              label="Coût d'inaction/mois"
              value={data.total_cost_monthly_meur ? `${data.total_cost_monthly_meur.toFixed(2)} M€` : "—"}
              color="#EF4444"
            />
            <KPI
              label="Coût d'inaction/an"
              value={data.total_cost_annual_meur ? `${data.total_cost_annual_meur.toFixed(1)} M€` : "—"}
              color="#EF4444"
            />
            <KPI label="Critiques" value={data.by_severity?.critique || 0} color="#EF4444" />
          </div>

          {/* Breakdown par type */}
          <div className="noam-card p-4" data-testid="radar-phantoms-by-type">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">
              Distribution par type (ordre doctrinal)
            </div>
            <div className="space-y-2">
              {(data.types || []).map((t, idx) => {
                const count = data.by_type?.[t] || 0;
                const cost = data.by_type_cost_monthly?.[t] || 0;
                const max = Math.max(...Object.values(data.by_type || { _: 1 }));
                const pct = max ? (count / max) * 100 : 0;
                return (
                  <div key={t} className="flex items-center gap-3" data-testid={`radar-phantoms-type-${t}`}>
                    <span
                      className="w-5 h-5 rounded text-[10px] flex items-center justify-center font-bold flex-shrink-0"
                      style={{ background: `${PHANTOM_TYPE_COLORS[t]}33`, color: PHANTOM_TYPE_COLORS[t] }}
                    >{idx + 1}</span>
                    <div className="text-xs text-zinc-200 w-40 truncate">
                      {(data.type_labels || {})[t] || t}
                    </div>
                    <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{ width: `${pct}%`, background: PHANTOM_TYPE_COLORS[t] }}
                      />
                    </div>
                    <div className="text-xs text-[#A78BFA] font-bold w-8 text-right">{count}</div>
                    <div className="text-[10px] text-red-300 w-20 text-right">
                      {cost > 0 ? `${cost.toFixed(2)} M€/m` : "—"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top sujets silencieux */}
          {data.top_subjects && data.top_subjects.length > 0 && (
            <div className="noam-card p-4" data-testid="radar-phantoms-top-subjects">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-[#A78BFA]" />
                Sujets prioritaires ({data.top_subjects.length})
              </div>
              <div className="space-y-1.5">
                {data.top_subjects.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => navigate(`/app/cartographie/${s.subject_type}/${s.subject_id}`)}
                    className="w-full text-left text-xs flex items-center gap-2.5 p-2.5 bg-white/[0.02] border border-white/5 rounded hover:border-[#A78BFA]/40 transition group"
                    data-testid={`radar-phantoms-top-${i}`}
                  >
                    <div
                      className="w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-bold flex-shrink-0"
                      style={{
                        borderColor: SEVERITY_COLOR[s.phantom_score >= 70 ? "critique" : s.phantom_score >= 40 ? "majeur" : "mineur"],
                        color: SEVERITY_COLOR[s.phantom_score >= 70 ? "critique" : s.phantom_score >= 40 ? "majeur" : "mineur"],
                      }}
                    >
                      {s.phantom_score}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-zinc-200 font-medium truncate">{s.subject_label || "(sans nom)"}</span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-white/5 text-zinc-400">
                          {s.subject_type === "opportunite" ? "Opp" : s.subject_type === "entite" ? "Entité" : "Risque"}
                        </span>
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        {s.phantoms_count} fantôme(s){s.total_cost_monthly_meur > 0 ? ` · ${s.total_cost_monthly_meur.toFixed(2)} M€/mois` : ""}
                        {s.scanned_at && ` · scanné ${new Date(s.scanned_at).toLocaleDateString("fr-CH")}`}
                      </div>
                    </div>
                    <ArrowUpRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-[#A78BFA] flex-shrink-0" />
                    <ChevronRight className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Footer info */}
          <div className="text-[10px] text-zinc-600 text-center">
            Agrégat généré {new Date(data.generated_at).toLocaleString("fr-CH")}
            {" · "}Cliquez un sujet pour ouvrir sa cartographie 360°
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, color }) {
  return (
    <div className="noam-card p-3">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-base font-bold mt-1 truncate" style={{ color }}>{value}</div>
    </div>
  );
}

const PHANTOM_TYPES_HELP = [
  {
    key: "ressource_pivot",
    icon: Users,
    color: "#EF4444",
    title: "Ressource pivot manquante",
    plain: "La personne ou l'actif dont tout dépend n'est pas identifié.",
    example: "Un deal à 5 M€ sans interlocuteur décisionnaire nommé : qui signe ?",
  },
  {
    key: "capture_economique",
    icon: Wallet,
    color: "#EF9F27",
    title: "Capture économique absente",
    plain: "L'opportunité existe, mais personne n'a défini comment vous gagnez de l'argent dessus.",
    example: "Un partenariat prometteur… sans modèle de revenus ni commission définie.",
  },
  {
    key: "profondeur_causale",
    icon: Search,
    color: "#F4D35E",
    title: "Cause non identifiée",
    plain: "Vous savez QUOI, mais pas POURQUOI. Sans cause, impossible d'agir.",
    example: "Un risque noté « perte du client X » sans la raison derrière : prix ? relation ? concurrent ?",
  },
  {
    key: "plan_action",
    icon: ClipboardList,
    color: "#A78BFA",
    title: "Plan d'action manquant",
    plain: "Un sujet important sans prochaine étape définie. Il dort, et le temps joue contre vous.",
    example: "Une opportunité qualifiée il y a 3 semaines… et toujours aucune action planifiée.",
  },
  {
    key: "reseau",
    icon: AlertCircle,
    color: "#378ADD",
    title: "Réseau introuvable",
    plain: "Aucun contact de votre Annuaire n'est relié à ce sujet. Vous êtes seul dessus.",
    example: "Un dossier immobilier à Genève, alors que votre Annuaire contient 3 contacts du secteur.",
  },
];

function PhantomExplainer({ onClose }) {
  const dismiss = () => {
    try { localStorage.setItem("radar:phantoms:help-seen", "1"); } catch (e) { console.debug("localStorage indisponible", e); }
    onClose();
  };
  return (
    <div className="noam-card p-5 border-[#A78BFA]/25 bg-[#A78BFA]/[0.04]" data-testid="radar-phantoms-explainer">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#A78BFA] text-xs font-bold uppercase tracking-wider">
            <Ghost className="w-3.5 h-3.5" /> Comment ça marche ?
          </div>
          <p className="text-xs text-zinc-300 mt-2 max-w-3xl leading-relaxed">
            La plupart des outils vous montrent ce que vous <em>avez</em>. Les fantômes vous montrent ce que
            vous <strong className="text-white">n'avez pas</strong> — et qui devrait être là. Chaque dossier incomplet
            est une décision retardée, un deal qui refroidit, un risque qui grossit en silence.
          </p>
          <ol className="text-xs text-zinc-400 mt-3 space-y-1 list-decimal list-inside">
            <li>Ouvrez une <strong className="text-zinc-200">Cartographie 360°</strong> (opportunité, entité ou risque) et lancez un <strong className="text-zinc-200">scan Phantom</strong>.</li>
            <li>Noam vérifie 5 éléments essentiels que tout dossier sérieux devrait contenir (liste ci-dessous).</li>
            <li>Chaque absence devient un fantôme, avec un <strong className="text-zinc-200">coût d'inaction estimé en M€/mois</strong> : ce que ce trou vous coûte tant qu'il n'est pas comblé.</li>
            <li>Cet onglet agrège tous les scans : les dossiers les plus « troués » remontent en priorité.</li>
          </ol>
        </div>
        <button onClick={dismiss} className="text-zinc-500 hover:text-zinc-200 flex-shrink-0" data-testid="radar-phantoms-explainer-close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-4">
        {PHANTOM_TYPES_HELP.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.key} className="p-3 rounded border border-white/5 bg-white/[0.02]" data-testid={`phantom-help-${t.key}`}>
              <div className="flex items-center gap-2">
                <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: t.color }} />
                <span className="text-xs font-semibold" style={{ color: t.color }}>{t.title}</span>
              </div>
              <p className="text-[11px] text-zinc-300 mt-1.5 leading-relaxed">{t.plain}</p>
              <p className="text-[10px] text-zinc-500 mt-1 italic leading-relaxed">Ex : {t.example}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
